import { useMutation, useQuery } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import BooleanField from '../../core/components/Fields/BooleanField.tsx';
import FormField from '../../core/components/FormField.tsx';
import Layout from '../../core/components/Layout.tsx';
import Loader from '../../core/components/Loader.tsx';
import Page from '../../core/components/Page';
import SaveStatus from '../../core/components/SaveStatus.tsx';
import SettingsFormCard from '../../core/components/SettingsFormCard.tsx';
import SettingsNavigation from '../../core/components/SettingsNavigation.tsx';
import { useToaster } from '../../core/components/Toast.tsx';
import { useApplicationContext } from '../../core/contexts/ApplicationContext.tsx';
import countFieldErrors from '../../core/utils/countFieldErrors.ts';
import { GET_COMPANY_DETAILS, UPDATE_AGENT_CHAT_ENABLED } from '../queries.ts';

const CUSTOMER_AGENTS_FORM_DEFAULT_VALUES = {
    agentChatEnabled: true,
};

const CustomerAgentsPage = () => {
    const { t } = useTranslation('customer');
    const { publishToast } = useToaster();
    const { refetchUser } = useApplicationContext();

    const form = useForm({ defaultValues: CUSTOMER_AGENTS_FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    const { data } = useQuery(GET_COMPANY_DETAILS, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only',
        variables: {},
    });
    const customer = data?.viewer?.customer;
    const [formReady, setFormReady] = useState(false);

    useEffect(() => {
        if (customer) {
            form.reset({
                ...CUSTOMER_AGENTS_FORM_DEFAULT_VALUES,
                agentChatEnabled: customer?.agentChatEnabled ?? true,
            });
            setFormReady(true);
        }
    }, [customer]);

    const [updateCustomer] = useMutation(UPDATE_AGENT_CHAT_ENABLED);
    const handleUpdateCustomer = async (formData) => {
        try {
            await updateCustomer({ variables: { input: { agentChatEnabled: formData.agentChatEnabled } } });
            await refetchUser?.();
            form.reset(formData);
            publishToast({ description: t('agents.form.updateSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('agents.form.updateError'), status: 'error' });
        }
    };

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header className="justify-between">
                    <Page.HeaderTitle>{t('agents.title')}</Page.HeaderTitle>
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6 relative">
                    {!formReady ? (
                        <Loader />
                    ) : (
                        <SettingsFormCard onSubmit={form.handleSubmit(handleUpdateCustomer)}>
                            <Controller
                                name="agentChatEnabled"
                                control={form.control}
                                render={({ field }) => (
                                    <FormField
                                        label={t('agents.form.chatEnabled')}
                                        error={formErrors.agentChatEnabled}
                                        helpText={t('agents.form.chatEnabledHelpText')}
                                    >
                                        <BooleanField {...field} value={field.value ? 'true' : ''} />
                                    </FormField>
                                )}
                            />

                            <SaveStatus
                                isDirty={form.formState.isDirty}
                                isSubmitting={form.formState.isSubmitting}
                                errorCount={countFieldErrors(formErrors)}
                            />
                        </SettingsFormCard>
                    )}
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default CustomerAgentsPage;
